// "Spot the Slip" — error-correction practice items for the calm practice deck.
//
// Each item is one Spanish sentence containing exactly ONE deliberate,
// pedagogically meaningful mistake — the kind an English-speaking A1/A2 learner
// actually makes — paired with the corrected sentence and a one-line reason.
// These map onto the Reference Shelf posters (ser/estar, por/para, gender,
// accents, gustar, pretérito/imperfecto, pronoun order, subjunctive), so the
// practice and the reference reinforce each other.
//
// The diff between `wrong` and `right` is computed at render time (word-level),
// so we only need the two full sentences plus metadata here. `tag` groups items
// for the filter chips; `slug` (optional) deep-links to the matching poster.
//
// Authored originally for Verde. Each row:
//   [ wrong, right, tag, why, slug ]

// Per-theme expansion rows (identity … media) live in standalone
// corrections-<theme>.js modules and are appended to BASE_ROWS below.
import { EXTRA_CORRECTION_ROWS } from "./corrections-expansion.js";

const BASE_ROWS = [
  // ---- ser vs estar ---------------------------------------------------
  ["Yo soy cansado hoy.", "Yo estoy cansado hoy.", "ser-estar",
    "Tiredness is a passing state, so it takes estar, not ser.", "ser-vs-estar"],
  ["La sopa es muy caliente ahora.", "La sopa está muy caliente ahora.", "ser-estar",
    "A temperature you can feel right now is a condition → estar.", "ser-vs-estar"],
  ["Mi hermana está médica.", "Mi hermana es médica.", "ser-estar",
    "A profession is part of who you are → ser.", "ser-vs-estar"],
  ["El concierto está a las ocho.", "El concierto es a las ocho.", "ser-estar",
    "An event taking place uses ser; estar would mean physical location.", "ser-vs-estar"],
  ["¿Dónde es el baño?", "¿Dónde está el baño?", "ser-estar",
    "Location of a thing or place is always estar.", "ser-vs-estar"],

  // ---- por vs para ----------------------------------------------------
  ["Estudio español para mi trabajo nuevo, así que practico por hablar mejor.",
    "Estudio español para mi trabajo nuevo, así que practico para hablar mejor.", "por-para",
    "Purpose / goal (\"in order to\") is para, not por.", "por-vs-para"],
  ["Gracias para tu ayuda.", "Gracias por tu ayuda.", "por-para",
    "Cause or motive (\"thanks because of\") takes por.", "por-vs-para"],
  ["Este regalo es por ti.", "Este regalo es para ti.", "por-para",
    "A recipient takes para.", "por-vs-para"],
  ["Salimos por Madrid mañana.", "Salimos para Madrid mañana.", "por-para",
    "Destination of movement is para.", "por-vs-para"],
  ["Pagué veinte euros para el libro.", "Pagué veinte euros por el libro.", "por-para",
    "An exchange (money for goods) is por.", "por-vs-para"],

  // ---- noun gender & agreement ----------------------------------------
  ["Tengo un problema grande con el mapa.", "Tengo un problema grande con el mapa.", "gender",
    "Trap check: el problema and el mapa are masculine despite the -a. (Already correct!)", "gender-agreement-of-nouns"],
  ["La problema es muy difícil.", "El problema es muy difícil.", "gender",
    "Greek-origin -ma words like problema are masculine → el.", "gender-agreement-of-nouns"],
  ["Me gusta la mano derecho.", "Me gusta la mano derecha.", "gender",
    "Mano is feminine even though it ends in -o, so the adjective is derecha.", "gender-agreement-of-nouns"],
  ["Es una día bonito.", "Es un día bonito.", "gender",
    "El día is masculine; pair it with un, not una.", "gender-agreement-of-nouns"],
  ["Las casas blancos son nuevas.", "Las casas blancas son nuevas.", "gender",
    "Adjectives agree in gender and number: feminine plural → blancas.", "adjective-agreement"],

  // ---- gustar & similar ----------------------------------------------
  ["Yo gusto el café.", "Me gusta el café.", "gustar",
    "Gustar means \"to please\": the coffee pleases me → me gusta el café.", "gustar-and-similar-verbs"],
  ["Me gusta los libros de historia.", "Me gustan los libros de historia.", "gustar",
    "The verb agrees with the thing liked; plural books → gustan.", "gustar-and-similar-verbs"],
  ["A mí me gusta correr y nadar, pero no me gustan bailar.",
    "A mí me gusta correr y nadar, pero no me gusta bailar.", "gustar",
    "With an action (infinitive) the verb stays singular → gusta.", "gustar-and-similar-verbs"],
  ["Te gustas el chocolate, ¿verdad?", "Te gusta el chocolate, ¿verdad?", "gustar",
    "Don't conjugate gustar to the person; match it to the thing (el chocolate).", "gustar-and-similar-verbs"],

  // ---- pretérito vs imperfecto ---------------------------------------
  ["Cuando era niño, fui a la playa todos los veranos.",
    "Cuando era niño, iba a la playa todos los veranos.", "preterito-imperfecto",
    "A repeated habit in the past is imperfecto → iba.", "a2-preterito-vs-imperfecto"],
  ["Ayer comía con mi familia y luego salía al cine.",
    "Ayer comí con mi familia y luego salí al cine.", "preterito-imperfecto",
    "Single, finished events in sequence take the pretérito.", "a2-preterito-vs-imperfecto"],
  ["Era las tres cuando sonaba el teléfono.",
    "Eran las tres cuando sonó el teléfono.", "preterito-imperfecto",
    "Background time stays imperfecto (eran); the interrupting event is pretérito (sonó).", "a2-preterito-vs-imperfecto"],

  // ---- object-pronoun order (RID) ------------------------------------
  ["¿El libro? Le lo di esta mañana.", "¿El libro? Se lo di esta mañana.", "pronoun-order",
    "le + lo becomes se lo (you can't say \"le lo\").", "object-pronouns"],
  ["Me un café trae, por favor.", "Tráeme un café, por favor.", "pronoun-order",
    "On a command the pronoun attaches to the end: tráeme.", "object-pronouns"],
  ["Lo me puedes explicar otra vez?", "¿Me lo puedes explicar otra vez?", "pronoun-order",
    "Indirect before direct: me lo, never lo me.", "object-pronouns"],

  // ---- present subjunctive (WEIRDO) ----------------------------------
  ["Quiero que vienes a la fiesta.", "Quiero que vengas a la fiesta.", "subjunctive",
    "A wish about someone else (two subjects + que) triggers the subjunctive → vengas.", "b1-present-subjunctive"],
  ["Es importante que estudias cada día.", "Es importante que estudies cada día.", "subjunctive",
    "Impersonal expressions of importance take the subjunctive → estudies.", "b1-present-subjunctive"],
  ["Espero que tienes un buen viaje.", "Espero que tengas un buen viaje.", "subjunctive",
    "Esperar que (hope/wish) flips the verb to the subjunctive → tengas.", "b1-present-subjunctive"],

  // ---- written accents ------------------------------------------------
  ["Mi medico vive en una ciudad pequeña.", "Mi médico vive en una ciudad pequeña.", "accents",
    "Médico is esdrújula — stress on the 3rd-to-last syllable always carries a written accent.", "stress-and-written-accents"],
  ["El cafe está frio esta mañana.", "El café está frío esta mañana.", "accents",
    "Café needs the accent (agudas ending in a vowel) and frío breaks the vowel rule.", "stress-and-written-accents"],

  // ---- common A1 mix-ups ---------------------------------------------
  ["Tengo veinte años viejo.", "Tengo veinte años.", "basics",
    "Age uses tener + años with no word for \"old\": tengo veinte años.", ""],
  ["Mi madre tiene calor y yo tengo veintiocho.",
    "Mi madre tiene calor y yo tengo veintiocho años.", "basics",
    "Keep años when stating age, even after a number.", ""],
  ["Hace dos años que vivo aquí, y soy de Noruega desde siempre.",
    "Hace dos años que vivo aquí, y soy de Noruega de toda la vida.", "basics",
    "\"Desde siempre\" is unidiomatic here; de toda la vida reads naturally.", ""],
];

// Stable id + structured shape for the view.
const ROWS = [...BASE_ROWS, ...EXTRA_CORRECTION_ROWS];
export const CORRECTIONS = ROWS.map(([wrong, right, tag, why, slug], i) => ({
  id: `corr-${i + 1}`,
  wrong,
  right,
  tag,
  why,
  slug: slug || "",
}));

// Tag registry — drives the filter chips. Order is intentional (mirrors the
// Reference Shelf), label is human-readable.
export const CORRECTION_TAGS = [
  { id: "ser-estar", label: "Ser vs Estar" },
  { id: "por-para", label: "Por vs Para" },
  { id: "gender", label: "Gender & Agreement" },
  { id: "gustar", label: "Gustar" },
  { id: "preterito-imperfecto", label: "Pretérito vs Imperfecto" },
  { id: "pronoun-order", label: "Pronoun Order" },
  { id: "subjunctive", label: "Subjunctive" },
  { id: "accents", label: "Accents" },
  { id: "basics", label: "Everyday Basics" },
];
